/**
 * Phone login overlay for returning operatives on the Start step.
 * Uses the Secret Missions phone endpoints (SMS 2FA).
 *
 * Styling is Tailwind. `accent` is authored per adventure, so it cannot be a
 * static class — it is set once on the root as a CSS custom property and read
 * back through arbitrary values.
 */
import { useEffect, type CSSProperties } from "react";
import {
  usePhoneLogin,
  type SignedIn,
} from "@/features/funnel/hooks/use-phone-login";
import { ACTION_BTN_CLASS } from "@/features/stepper/steptypes/step-shell";

const FIELD_CLASS =
  "w-full rounded-lg border border-[var(--sm-accent-edge)] bg-black/35 px-[14px] py-3 text-base text-[#f4eddb] outline-none";

const PRIMARY_BTN_CLASS = `${ACTION_BTN_CLASS} w-full rounded-lg px-4 py-3 text-[14.5px] font-semibold disabled:cursor-wait disabled:opacity-70`;

const GHOST_BTN_CLASS =
  "mt-2 w-full cursor-pointer rounded-lg border-none bg-transparent px-4 py-3 text-[14.5px] font-medium text-[#a99d86] disabled:cursor-wait disabled:opacity-70";

const LABEL_CLASS =
  "mb-2 block text-xs tracking-[1px] text-[#a99d86] uppercase";

interface LoginModalProps {
  open: boolean;
  onClose: () => void;
  /** Runs after a successful verify, before the modal closes. */
  onSignedIn?: (who?: SignedIn) => void;
  accent?: string;
  /** the toast on success ("" for none) */
  successMessage?: string;
  /** the heading (default "Login") */
  title?: string;
  /** see usePhoneLogin */
  signInOnly?: boolean;
}

export function LoginModal({
  open,
  onClose,
  onSignedIn,
  accent = "#d6a94e",
  successMessage,
  title = "Login",
  signInOnly,
}: LoginModalProps) {
  const login = usePhoneLogin({
    successMessage,
    signInOnly,
    // Closed below, after this runs; see `close`.
    onSignedIn: (who) => {
      onSignedIn?.(who);
      close();
    },
  });
  const { phone, setPhone, code, setCode, attemptId, busy } = login;

  // However the modal leaves — closed, or unmounted after a successful
  // verify — the window goes back to the top.
  useEffect(
    () => () => {
      requestAnimationFrame(() => window.scrollTo(0, 0));
    },
    [],
  );

  if (!open) return null;

  // Hex-with-alpha suffixes are computed here rather than with color-mix in a
  // class, so the alpha stays legible next to the colour it applies to.
  const vars = {
    "--sm-accent-edge": `${accent}6b`,
    "--sm-accent-strong": `${accent}80`,
    "--sm-btn-text": "#e6d3a4",
  } as CSSProperties;

  // Focusing an input opens the iOS keyboard, which scrolls the window down.
  // The funnel pins `overflow: hidden` on the document, so nothing scrolls it
  // back once the keyboard goes away and the screen sits stuck below its own
  // top. Put it back by hand whenever the modal leaves, however it leaves.
  const restoreScroll = () => {
    requestAnimationFrame(() => window.scrollTo(0, 0));
  };

  function close() {
    login.reset();
    restoreScroll();
    onClose();
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Login"
      style={vars}
      className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4 font-sans"
      onClick={close}
      onKeyDown={(e) => e.key === "Escape" && close()}
    >
      <div
        className="w-full max-w-[360px] rounded-xl border border-[var(--sm-accent-edge)] bg-[rgb(18_16_18)] px-[22px] pt-[22px] pb-[18px] shadow-[0_20px_60px_rgba(0,0,0,.55)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* the close button sits apart from the heading (a long heading
            wraps up against it otherwise, and reads as part of it) */}
        <div className="mb-[14px] flex items-start justify-between gap-4">
          <h2 className="m-0 text-lg leading-snug font-bold tracking-[0.2px] text-[#f4eddb]">
            {title}
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={close}
            className="-mt-1 -mr-2 grid size-9 shrink-0 cursor-pointer place-items-center rounded-full border border-white/15 bg-white/[.06] text-[20px] leading-none text-[#cfc4ad] hover:bg-white/[.12]"
          >
            ×
          </button>
        </div>

        {attemptId == null ? (
          <form onSubmit={login.sendCode}>
            <label className={LABEL_CLASS}>Phone</label>
            <input
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="555-555-5555"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={FIELD_CLASS}
              autoFocus
            />
            <button
              type="submit"
              disabled={busy || !login.phoneValid}
              className={`mt-[14px] ${PRIMARY_BTN_CLASS}`}
            >
              {busy ? "Sending…" : "Send code"}
            </button>
          </form>
        ) : (
          <form onSubmit={login.verify}>
            <label className={LABEL_CLASS}>Code</label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className={FIELD_CLASS}
              autoFocus
            />
            <button
              type="submit"
              disabled={busy}
              className={`mt-[14px] ${PRIMARY_BTN_CLASS}`}
            >
              {busy ? "Checking…" : "Verify"}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={login.changeNumber}
              className={GHOST_BTN_CLASS}
            >
              Use a different number
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
